import { plural } from '@ipfabric/frontend-shared-assets/utils/plural';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { createTextColumn, discoveryTasks } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { unsafeAssert } from '@ipfabric/utils/types/unsafeAssert';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type { TableCellContentRenderFunction } from 'components/Table/model/TableCellContentRenderFunction';
import type {
  TableBulkAction,
  TableBulkActionClickEvent,
  TableRowIdValue,
} from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { pipe } from 'fp-ts/function';
import { useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { indexBy } from 'utils/object/indexBy';
import { DisabledDiscoveryTaskEditModal } from './DisabledDiscoveryTaskEditModal';
import type { DiscoveryTasksRowData } from './DiscoveryTasksRowData';

type Props = {
  form: UseFormReturn<{ disabledDiscoveryTasks: DiscoveryTasksRowData[] }>;
};
type DiscoveryTasksTableRowData = DiscoveryTasksRowData & { taskName: string };

export const DisabledDiscoveryTasks = (props: Props) => {
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const [editedRule, setEditedRule] = useState<
    DiscoveryTasksRowData | 'new' | null
  >(null);

  const updateFormValue = (
    updater: (value: DiscoveryTasksRowData[]) => DiscoveryTasksRowData[],
  ) => {
    props.form.setValue(
      'disabledDiscoveryTasks',
      updater(props.form.getValues().disabledDiscoveryTasks),
      { shouldDirty: true },
    );
  };

  const deleteTasksByIds = useStableCallback(async (ids: Set<string>) => {
    updateFormValue((tasks) => tasks.filter((task) => !ids.has(task.id)));
  });

  const requestDeleteEntries = useStableCallback(
    async (ids: Set<string>, label?: string): Promise<boolean> => {
      if (
        await confirmDeleteDialog.show(
          {
            header: `${plural(ids.size, 'rule')} will be deleted`,

            subHeader: label
              ? `Do you really want to delete the rule for ${label}?`
              : `Do you really want to delete ${ids.size} selected discovery ${plural(ids.size, 'task')}?`,
            btnConfirm: 'Delete',
            btnSecondaryConfirm: 'Cancel',
          },
          {
            btnConfirmDataTestId: 'tasks-dialog-remove-confirm',
          },
        )
      ) {
        deleteTasksByIds(ids);
        return true;
      }
      return false;
    },
  );

  const discoveryTasksById = useMemo(
    () => pipe(discoveryTasks, indexBy(prop('id'))),
    [],
  );

  const data = useWatch({
    control: props.form.control,
    name: 'disabledDiscoveryTasks',
  });

  const tableData = useMemo<DiscoveryTasksTableRowData[]>(
    () =>
      (data ?? []).map((task) => ({
        ...task,
        taskName: discoveryTasksById[task.taskId]?.label ?? task.taskId,
      })),
    [data, discoveryTasksById],
  );

  const columns = useMemo(
    () => [
      createTextColumn({
        key: 'taskName',
        title: 'Task name',
        renderContent: (({ row }) => ({
          content: (
            <>
              {row.taskName}
              <TooltipIcon
                content={discoveryTasksById[row.taskId]?.description ?? ''}
                iconProps={{
                  inline: true,
                }}
                className='ml-2'
                size='sm'
              />
            </>
          ),
        })) satisfies TableCellContentRenderFunction<DiscoveryTasksTableRowData>,
      }),
      createTextColumn({
        key: 'label',
        title: 'Description',
      }),
      createFrontendOnlyColumn({
        key: 'action',
        disabledSort: true,
        title: 'Actions',
        render: (rowData: DiscoveryTasksTableRowData) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              dataTestId={`disabled-discovery-tasks-${rowData.label}-edit-icon`}
              variant='plain'
              icon='fa-pen'
              size='small'
              onClick={() => {
                setEditedRule(rowData);
              }}
              type='button'
            />
            <TableButton
              dataTestId={`disabled-discovery-tasks-${rowData.label}-remove-icon`}
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={() =>
                requestDeleteEntries(
                  new Set([rowData.id]),
                  discoveryTasksById[rowData.taskId].label,
                )
              }
              type='button'
            />
          </div>
        ),
      }),
    ],
    [discoveryTasksById],
  );

  const bulkActions = useMemo(
    (): TableBulkAction[] => [
      {
        key: 'delete',
        title: 'Delete',
        accent: 'negative',
        disableAutoDeselect: true,
        icon: 'fa-trash-can',
        onClick: async ({
          selectedRows,
          deselectAll,
        }: TableBulkActionClickEvent) => {
          if (
            await requestDeleteEntries(
              unsafeAssert<Set<TableRowIdValue>, Set<string>>(selectedRows),
            )
          ) {
            deselectAll();
          }
        },
      },
    ],
    [],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: tableData,
  });

  return (
    <>
      <Table
        bulkActions={bulkActions}
        className='flex-1 bg-transparent'
        columns={columns}
        loadRowIdsForSelectAll='fromData'
        rowHeight={44}
        rowIdField='id'
        title='Rule list'
        menuItems={[
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='add-new'
                variant='outline'
                size='small'
                icon='fa-plus-circle'
                onClick={() => {
                  setEditedRule('new');
                }}
              >
                Add new
              </Button>
            ),
          },
        ]}
        {...frontendSimpleFilter}
      />
      {editedRule !== null && (
        <DisabledDiscoveryTaskEditModal
          editedRule={editedRule}
          onClose={() => {
            setEditedRule(null);
          }}
          onSubmit={(rule) => {
            if (editedRule === 'new') {
              updateFormValue((rules) => [...rules, rule]);
            } else {
              updateFormValue((rules) =>
                rules.map((r) => (r.id === rule.id ? rule : r)),
              );
            }
            setEditedRule(null);
          }}
        />
      )}
      {confirmDeleteDialog.render()}
    </>
  );
};
